import { keyBy, mapValues } from 'lodash';
import { Task, taskZod } from 'packages/lib/api/annotations/contract/task';
import { patchBulk, apiGet } from 'packages/lib/api/annotations/rest';
import { DataCollisionError } from 'packages/lib/utils/error';
import { incrementTaskState } from 'packages/task-man/service/edit/pickup-task';
import {
  allEqualAnnotationState,
  allHaveAssignee,
} from 'packages/task-man/service/utilities';

/**
 * Used for both single-task (tasks.length = 1) and multi-task (tasks.length >= 1) workflows
 *
 * This Task Management service method is used specifically for use-cases where users
 * pickup and self-assign sets of N tasks. It changes both the Task.annotationState
 * and the Task.assignee for every provided task.
 *
 * If any of the given tasks are already assigned, or in an invalid annotationState, it will throw.
 *
 * Consumers of this method are responsible for fetching a different task in the case that
 * the provided task is already assigned or otherwise unable to be assigned.
 */
export const pickupManyTasks = async (
  tasks: Task[],
  userEmail: string
): Promise<Task[]> => {
  if (!tasks.length) {
    throw new Error(
      'Cannot pickupManyTasks if there are zero tasks available. This is probably a bug.'
    );
  }
  if (!allEqualAnnotationState(tasks)) {
    throw new Error(
      'All tasks must be in the same AnnotationState in order to pick up many tasks at once.'
    );
  }
  if (!allHaveAssignee(tasks)) {
    throw new Error('All tasks must be unassigned in order to pick them up.');
  }
  /**
   * We know:
   *  - at least 1 task exists
   *  - all tasks are unassigned
   *  - all are in the same AnnotationState
   * so we only need to calculate the PATCH payload for the first task.
   */
  const { payload } = await incrementTaskState(tasks[0], userEmail);
  const manyPayloads = mapValues(keyBy(tasks, 'token'), () => payload);
  /**
   * TODO: Integrate user task-state-changed metrics
   */

  await patchBulk(`/tasks/bulk`, manyPayloads);

  /**
   * NOTE: We re-fetch all of the tasks on the front-end in order to detect
   * and prevent collisions if more than one user simultaenously pickup the
   * same task set.
   *
   * If we re-fetch and the assignee is incorrect, we assume that the current
   * user lost the race condition / collision.
   */
  const next = await Promise.all(
    tasks.map(async (t) => {
      return apiGet(`/tasks/${t.token}`, taskZod.parse);
    })
  );
  if (next.find((t) => t.assignee !== userEmail)) {
    throw new DataCollisionError(
      'Task Assignment failed. Possible Task Pickup collision. Please retry unless the problem persists.'
    );
  }
  if (next.find((t) => t.annotationState !== payload.annotationState)) {
    throw new Error('Task Annotation State failed to update as expected.');
  }
  return next;
};
